import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: cn(
          "text-sm font-medium",
          props.captionLayout?.toString().includes("dropdown") && "hidden"
        ),
        caption_dropdowns: "flex justify-center gap-1.5 z-10",
        dropdown: "bg-background text-xs font-semibold focus-visible:outline-none cursor-pointer hover:bg-muted/50 rounded-md px-1.5 py-1 border border-border/50 text-foreground transition-colors",
        dropdown_month: "flex items-center gap-1 [&>span]:hidden",
        dropdown_year: "flex items-center gap-1 [&>span]:hidden",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative h-9 w-9 text-center text-sm p-0 focus-within:relative focus-within:z-20",
          // Range middle: light orange background, clipped to cell bounds
          "[&:has([aria-selected].day-range-middle)]:bg-primary/10",
          // Range ends: fully rounded
          "[&:has([aria-selected].day-range-end)]:rounded-r-full",
          "first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full",
          // Range start cell
          "[&:has(.day-range-start)]:rounded-l-full",
          // Ensure overflow is hidden so bg doesn't leak
          "overflow-hidden",
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-full",
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_range_middle:
          "day-range-middle aria-selected:bg-primary/10 aria-selected:text-foreground aria-selected:rounded-none",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-full",
        day_today:
          "text-primary font-semibold bg-transparent aria-selected:bg-primary aria-selected:text-primary-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-primary/5 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
export { Calendar };
